import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Facebook, Youtube, Mail, Phone, Upload } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AdminLogin from './AdminLogin';
import VideoUpload from './VideoUpload';

const Footer = () => {
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const { isAuthenticated, logout } = useAuth();

  const handleAdminClick = () => {
    if (isAuthenticated) {
      setShowVideoUpload(true);
    } else {
      setShowAdminLogin(true);
    }
  };

  return (
    <>
      <footer className="bg-slate-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">DC</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">Dan Casey</h3>
                  <p className="text-gray-400 text-sm">Registered Investment Adviser Representative</p>
                </div>
              </div>
              <p className="text-gray-400 mb-4">
                At Bridgeriver Advisors, we aim to make the complex simple. 
                Let us help you work toward your financial goals.
              </p>
              <div className="flex space-x-4">
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-slate-700">
                  <a href="https://www.facebook.com/Bridgeriver" target="_blank" rel="noopener noreferrer">
                    <Facebook className="w-5 h-5" />
                  </a>
                </Button>
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-slate-700">
                  <a href="https://www.youtube.com/@bridgeriveradvisors" target="_blank" rel="noopener noreferrer">
                    <Youtube className="w-5 h-5" />
                  </a>
                </Button>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="/" className="text-gray-400 hover:text-white transition-colors">Home</a></li>
                <li><a href="/events" className="text-gray-400 hover:text-white transition-colors">Events</a></li>
                <li><a href="#appearances" className="text-gray-400 hover:text-white transition-colors">TV Appearances</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors">About</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors">Services</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-gray-400">(248) 785-3734</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-gray-400">info@bridgeriverllc.com</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Important Disclosures */}
          <div className="border-t border-gray-700 mt-8 pt-8">
            <h4 className="text-sm font-semibold text-gray-300 mb-3">Important Disclosures</h4>
            <div className="space-y-3 text-xs text-gray-500 leading-relaxed">
              <p>Bridgeriver Advisors is a registered investment adviser in the State of Michigan. Registration does not imply a certain level of skill or training. Bridgeriver Advisors may only transact business in states where it is properly registered or qualifies for an exemption or exclusion from registration requirements. Information on this website is not intended for residents of any state where such information would be contrary to local law or regulation.</p>
              <p>The information contained on this website is for educational and informational purposes only and does not constitute investment advice, personalized financial advice, a solicitation, or an offer to buy or sell any securities. Nothing on this website should be construed as personalized investment, tax, or legal advice. You should consult with a qualified professional before making any financial decisions. Bridgeriver Advisors does not provide tax or legal advice.</p>
              <p>Investing involves risk, including the potential loss of principal. No investment strategy can guarantee a profit or protect against loss in periods of declining values. Past performance is not indicative of future results. Any references to protection benefits, safety, security, or steady income generally refer to fixed insurance products, not investment securities.</p>
              <p>Insurance products and services are offered through Panic Proof Retirement, a separate entity from Bridgeriver Advisors. Investment advisory services and insurance products are separate and distinct services. Clients are under no obligation to purchase insurance products through Panic Proof Retirement in connection with any investment advisory services provided by Bridgeriver Advisors.</p>
              <p>This website may contain links to third-party websites provided for convenience only. Bridgeriver Advisors does not endorse, approve, or take responsibility for the content, products, or services available through any third-party website.</p>
              <p>Bridgeriver Advisors is committed to protecting your privacy. Our Privacy Policy and Form ADV Part 2A (Firm Brochure) and Part 2B (Brochure Supplement) — which describe our business practices, fees, and conflicts of interest — are available upon request.</p>
            </div>
          </div>

          {/* FINRA BrokerCheck Disclaimer */}
          <div className="border-t border-gray-700 mt-8 pt-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-slate-900/50 rounded-lg p-4">
              <div className="flex-shrink-0">
                <a
                  href="https://brokercheck.finra.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img
                    src="https://www.finra.org/sites/default/files/BrokerCheck.png"
                    alt="FINRA BrokerCheck"
                    className="h-8 w-auto brightness-200"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                  <span className="hidden text-sm font-semibold text-white underline">FINRA BrokerCheck</span>
                </a>
              </div>
              <div className="text-xs text-gray-400 leading-relaxed">
                <p>
                  Check the background of your financial professional on FINRA's{' '}
                  <a
                    href="https://brokercheck.finra.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    BrokerCheck
                  </a>
                  . The content is developed from sources believed to be providing accurate information. The information in this material is not intended as tax or legal advice. Please consult legal or tax professionals for specific information regarding your individual situation. Some of this material was developed and produced by FMG Suite to provide information on a topic that may be of interest. FMG Suite is not affiliated with the named representative, broker-dealer, state- or SEC-registered investment advisory firm. The opinions expressed and material provided are for general information and should not be considered a solicitation for the purchase or sale of any security.
                </p>
              </div>
            </div>
          </div>


          <div className="border-t border-gray-700 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 mb-4 md:mb-0">
                © 2026 Bridgeriver Advisors. All rights reserved.
              </p>
              
              {/* Admin section at very bottom */}
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAdminClick}
                  className="text-gray-500 hover:text-gray-300 text-xs"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  <span>{isAuthenticated ? 'Upload' : 'Admin'}</span>
                </Button>
                
                {isAuthenticated && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className="text-gray-500 hover:text-gray-300 text-xs"
                  >
                    Logout
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </footer>

      
      <AdminLogin 
        open={showAdminLogin} 
        onOpenChange={setShowAdminLogin} 
      />
      
      <VideoUpload 
        open={showVideoUpload} 
        onOpenChange={setShowVideoUpload} 
      />
    </>
  );
};

export default Footer;
